import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Check, Award } from 'lucide-react';
import { getSiteSettings, getPaymentTiers } from '../services/api';
import { FreeTierIcon, BronzeTierIcon, SilverTierIcon, GoldTierIcon } from '../components/TierIcons';

const LandingPage = () => {
  const navigate = useNavigate();
  
  const defaultSettings = {
    hero: {
      badge: 'Fictional Crime Investigation Platform',
      headline: "DEADLINE by Daria",
      description: "Dive deep into exclusive fictional crime investigations. Uncover case files, evidence, and behind-the-scenes analysis not available anywhere else. Join fellow detectives in solving the mysteries.",
      bannerImage: 'https://customer-assets.emergentagent.com/job_membership-core/artifacts/7p1ryw8r_household%20mishaps%20%28394%29.JPG',
      ctaButtonText: 'Start Investigation',
      secondaryButtonText: 'Sign In'
    },
    tiers: {
      sectionTitle: 'Case Access Levels',
      sectionDescription: 'Choose your investigative clearance. Higher tiers unlock deeper case files and exclusive evidence.'
    },
    features: {
      sectionTitle: 'Inside the Detective Bureau',
      sectionDescription: 'Fictional detective-themed photosets and murder mystery thriller films',
      feature1Title: 'Exclusive Case Files',
      feature1Description: 'Access detailed fictional case files, staged crime scene photosets, and thriller films',
      feature2Title: 'Direct Expert Access',
      feature2Description: 'Connect directly with investigative insights and expert commentary',
      feature3Title: 'Detective Community',
      feature3Description: 'Join discussions with fellow investigators and collaborate on theories'
    },
    cta: {
      title: 'Ready to Join the Investigation?',
      description: 'Start with basic access and upgrade anytime for deeper case penetration.',
      buttonText: 'Begin Your Investigation'
    }
  };

  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [tiers, setTiers] = useState([]);

  const loadSettings = useCallback(async () => {
    try {
      const data = await getSiteSettings();
      setSettings({...defaultSettings, ...data});
    } catch (error) {
      // Use defaults on error
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTiers = useCallback(async () => {
    try {
      const data = await getPaymentTiers();
      let tiersArray = [];
      if (Array.isArray(data)) {
        tiersArray = data;
      } else if (data.tiers && typeof data.tiers === 'object') {
        tiersArray = Object.entries(data.tiers).map(([key, tier]) => ({
          tier_id: `tier_${key}`,
          ...tier
        }));
      }
      const sortedTiers = tiersArray.sort((a, b) => (a.price || 0) - (b.price || 0));
      setTiers(sortedTiers);
    } catch (error) {
    }
  }, []);

  useEffect(() => {
    loadSettings();
    loadTiers();
  }, [loadSettings, loadTiers]);

  const getTierIcon = (tierName) => {
    const name = tierName?.toLowerCase();
    switch (name) {
      case 'bronze': return <BronzeTierIcon className="w-6 h-6" />;
      case 'silver': return <SilverTierIcon className="w-6 h-6" />;
      case 'gold': return <GoldTierIcon className="w-6 h-6" />;
      default: return <FreeTierIcon className="w-6 h-6" />;
    }
  };

  return (
    <div className="min-h-screen bg-black text-gray-100">
      {/* Hero Section with Retro Detective Aesthetic */}
      <section className="relative py-20 px-4 overflow-hidden min-h-[700px] border-b-4 border-amber-600">
        {/* Film grain texture overlay */}
        <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxwYXRoIGQ9Ik0wIDBoMzAwdjMwMEgweiIgZmlsdGVyPSJ1cmwoI2EpIiBvcGFjaXR5PSIuMDUiLz48L3N2Zz4=')]" style={{backgroundSize: '150px'}}></div>
        
        {/* Dark overlay with vintage vignette */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80 z-10"></div>
        <div className="absolute inset-0 shadow-[inset_0_0_200px_rgba(0,0,0,0.6)] z-10"></div>
        
        {/* Banner Image */}
        <div className="absolute inset-0 opacity-60">
          <img 
            src={settings.hero.bannerImage}
            alt="Crime Scene"
            className="w-full h-full object-cover"
            style={{filter: 'sepia(0.2) contrast(1.05) saturate(0.9)'}}
          />
        </div>

        {/* Yellow police tape effect */}
        <div className="absolute top-16 left-0 right-0 h-12 bg-amber-400 transform -rotate-2 opacity-90 z-20 flex items-center justify-center font-headline text-black text-sm tracking-widest">
          <span className="animate-pulse">⚠ INVESTIGATION IN PROGRESS - AUTHORIZED PERSONNEL ONLY ⚠</span>
        </div>

        <div className="container mx-auto max-w-4xl relative z-20 mt-16">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Badge className="mb-6 bg-amber-900/70 text-amber-200 border-amber-600 font-typewriter text-xs px-4 py-1" variant="outline">
              {settings.hero.badge}
            </Badge>
          </motion.div>
          
          {/* Main Logo Image */}
          <motion.div 
            className="mb-8 flex justify-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <img 
              src="/logo.png" 
              alt="Deadline by Daria" 
              className="max-h-40 md:max-h-52 lg:max-h-64 w-auto object-contain animate-flicker"
              style={{ 
                mixBlendMode: 'screen'
              }}
              data-testid="hero-logo"
            />
          </motion.div>

          <motion.div 
            className="max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed drop-shadow-lg font-typewriter bg-black/40 p-6 border-l-4 border-amber-500">
              {settings.hero.description}
            </p>
          </motion.div>

          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <Button 
              size="lg" 
              onClick={() => navigate('/signup')} 
              className="text-lg px-10 py-6 bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider border-2 border-amber-500 shadow-[0_0_20px_rgba(251,191,36,0.4)] transition-all hover:shadow-[0_0_30px_rgba(251,191,36,0.6)] hover:scale-105"
            >
              {settings.hero.ctaButtonText}
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              onClick={() => navigate('/login')} 
              className="text-lg px-10 py-6 border-2 border-amber-600 text-amber-500 hover:bg-amber-950/50 font-headline tracking-wider"
            >
              {settings.hero.secondaryButtonText}
            </Button>
          </motion.div>

          {/* Case number stamp */}
          <motion.div 
            className="mt-8 text-center text-amber-600/60 font-typewriter text-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.8 }}
          >
            CASE FILE #DD-2024-001 | EST. 2024 | CONFIDENTIAL
          </motion.div>
        </div>
      </section>

      {/* Tiers Section - Detective Style */}
      <section className="py-20 px-4 bg-gradient-to-b from-black via-gray-900 to-black relative">
        {/* Subtle texture */}
        <div className="absolute inset-0 opacity-5 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCI+PHBhdGggZD0iTTAgMGg2MHY2MEgweiIgZmlsbD0iIzAwMCIvPjxwYXRoIGQ9Ik0zMCAzMG0tMiAwYTIgMiAwIDEgMSA0IDBhMiAyIDAgMSAxLTQgMCIgZmlsbD0iI2ZmZiIvPjwvc3ZnPg==')]"></div>
        
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-12">
            <div className="inline-block mb-4">
              <div className="bg-amber-900/30 border-2 border-amber-600 px-6 py-2 inline-block transform -rotate-1">
                <h2 className="text-3xl md:text-5xl font-headline text-amber-500 tracking-wider">
                  {settings.tiers.sectionTitle}
                </h2>
              </div>
            </div>
            <p className="text-lg text-gray-300 font-typewriter max-w-2xl mx-auto">
              {settings.tiers.sectionDescription}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {tiers.map((tier, index) => {
              const isTopTier = index === tiers.length - 1;
              const isFree = tier.price === 0;
              const accessLevel = index + 1;
              
              return (
                <Card 
                  key={tier.tier_id || tier.id || index} 
                  className={`relative overflow-hidden transition-all hover:scale-105 bg-gray-900/90 border-2 backdrop-blur ${
                    isTopTier ? 'border-amber-500 shadow-[0_0_30px_rgba(251,191,36,0.3)]' : 'border-gray-700'
                  }`}
                >
                  {isTopTier && (
                    <div className="absolute top-0 right-0 bg-amber-600 text-black px-3 py-1 text-xs font-headline tracking-wider">
                      FULL ACCESS
                    </div>
                  )}

                  <CardHeader className="pt-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-3 rounded-lg bg-amber-900/30 border border-amber-700">
                        <div className="text-amber-500">
                          {getTierIcon(tier.name)}
                        </div>
                      </div>
                      <CardTitle className="text-2xl font-headline text-gray-100 tracking-wide">{tier.name}</CardTitle>
                    </div>
                    <div className="mb-3">
                      <span className="text-4xl font-bold text-amber-500 font-headline">
                        {isFree ? 'FREE' : `$${tier.price}`}
                      </span>
                      {!isFree && (
                        <span className="text-base text-gray-500 font-typewriter">/month</span>
                      )}
                    </div>
                    <CardDescription className="text-gray-400 font-typewriter text-sm">{tier.description}</CardDescription>
                  </CardHeader>

                  <CardContent>
                    <ul className="space-y-3">
                      {(tier.benefits || []).map((benefit, idx) => (
                        <li key={`${tier.id}-benefit-${idx}`} className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-300">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>

                  <CardFooter>
                    <Button 
                      className={`w-full font-headline tracking-wider ${
                        isTopTier 
                          ? 'bg-amber-600 hover:bg-amber-700 text-black shadow-[0_0_15px_rgba(251,191,36,0.3)]' 
                          : 'bg-gray-800 hover:bg-gray-700 border border-amber-900'
                      }`}
                      onClick={() => navigate(isFree ? '/signup' : '/tiers')}
                    >
                      {isFree ? 'OPEN CASE' : 'SUBSCRIBE'}
                    </Button>
                  </CardFooter>
                </Card>
              );
            })}
          </div>

          <div className="mt-10 text-center">
            <button
              type="button"
              onClick={() => navigate('/ranks')}
              className="inline-flex items-center gap-2 rounded-full border border-amber-600/60 bg-amber-900/20 px-6 py-2.5 text-sm font-typewriter text-amber-200 transition-colors hover:bg-amber-900/40 hover:text-amber-100"
              data-testid="landing-loyalty-blurb"
            >
              <Award className="w-4 h-4 text-amber-400" />
              Stay on the case — paid members climb the ranks &amp; unlock loyalty rewards over time
              <span className="text-amber-500">→</span>
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-black border-t-2 border-amber-900/30">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-headline mb-4 text-amber-500 tracking-wider">
              {settings.features.sectionTitle}
            </h2>
            <p className="text-lg text-gray-400 font-typewriter">
              {settings.features.sectionDescription}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center space-y-3 p-6 bg-gray-900/50 border border-amber-900/30 hover:border-amber-700/50 transition-all">
              <div className="w-16 h-16 bg-amber-900/20 border-2 border-amber-700/50 rounded-lg flex items-center justify-center mx-auto text-amber-500">
                <BronzeTierIcon className="w-8 h-8" />
              </div>
              <h3 className="font-headline text-xl text-gray-200 tracking-wide">{settings.features.feature1Title}</h3>
              <p className="text-sm text-gray-400 font-typewriter">
                {settings.features.feature1Description}
              </p>
            </div>

            <div className="text-center space-y-3 p-6 bg-gray-900/50 border border-amber-900/30 hover:border-amber-700/50 transition-all">
              <div className="w-16 h-16 bg-amber-900/20 border-2 border-amber-700/50 rounded-lg flex items-center justify-center mx-auto text-amber-500">
                <SilverTierIcon className="w-8 h-8" />
              </div>
              <h3 className="font-headline text-xl text-gray-200 tracking-wide">{settings.features.feature2Title}</h3>
              <p className="text-sm text-gray-400 font-typewriter">
                {settings.features.feature2Description}
              </p>
            </div>

            <div className="text-center space-y-3 p-6 bg-gray-900/50 border border-amber-900/30 hover:border-amber-700/50 transition-all">
              <div className="w-16 h-16 bg-amber-900/20 border-2 border-amber-700/50 rounded-lg flex items-center justify-center mx-auto text-amber-500">
                <GoldTierIcon className="w-8 h-8" />
              </div>
              <h3 className="font-headline text-xl text-gray-200 tracking-wide">{settings.features.feature3Title}</h3>
              <p className="text-sm text-gray-400 font-typewriter">
                {settings.features.feature3Description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Evidence Locker Style */}
      <section className="py-20 px-4 bg-gradient-to-b from-amber-950/20 to-black border-t-4 border-amber-600">
        <div className="container mx-auto max-w-2xl text-center">
          <div className="mb-6 text-amber-500">
            <FreeTierIcon className="w-20 h-20 mx-auto mb-4" />
          </div>
          <h2 className="text-4xl md:text-5xl font-headline mb-4 text-gray-100 tracking-wider">
            {settings.cta.title}
          </h2>
          <p className="text-lg mb-8 text-gray-300 font-typewriter">
            {settings.cta.description}
          </p>
          <Button 
            size="lg" 
            onClick={() => navigate('/signup')} 
            className="text-xl px-12 py-6 bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-widest border-2 border-amber-500 shadow-[0_0_25px_rgba(251,191,36,0.4)]"
          >
            {settings.cta.buttonText}
          </Button>
          
          {/* Evidence tag */}
          <div className="mt-8 inline-block bg-amber-900/30 border border-amber-700 px-6 py-2 font-typewriter text-xs text-amber-500">
            CLEARANCE REQUIRED | AUTHORIZED ACCESS ONLY
          </div>
        </div>
      </section>

      {/* How we make our content */}
      <section className="bg-gray-950/80 border-t border-amber-900/20 py-14" data-testid="how-we-make-section">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="inline-block bg-amber-900/30 border border-amber-700 px-4 py-1 font-typewriter text-xs text-amber-500 mb-4">
            BEHIND THE SCENES
          </div>
          <h2 className="text-2xl md:text-3xl font-headline text-amber-500 tracking-wider mb-4">HOW WE MAKE OUR CONTENT</h2>
          <p className="text-gray-300 font-typewriter leading-relaxed md:text-lg">
            Every case file, photoset and film on Deadline by Daria is a scripted scene — written, staged and performed
            by the adult performers who own and operate this business. It's fictional detective-themed entertainment
            inspired by classic suspense cinema and TV crime dramas: the "crime scenes" are prop-dressed sets, the
            "victims" are actors, and nobody is ever harmed.
          </p>
          <div className="mt-6 grid gap-3 sm:grid-cols-3 text-sm font-typewriter text-gray-400">
            <div className="border border-amber-900/30 bg-black/40 p-4">Scripted stories, staged like a film set</div>
            <div className="border border-amber-900/30 bg-black/40 p-4">Performed by consenting adults</div>
            <div className="border border-amber-900/30 bg-black/40 p-4">Not explicit — no nudity, no real violence, no minors</div>
          </div>
          <a href="/content-policy" className="inline-block mt-5 text-amber-500 hover:text-amber-400 font-typewriter text-sm underline" data-testid="how-we-make-policy-link">
            Read our Content Policy →
          </a>
        </div>
      </section>

      {/* Disclaimer */}
      <div className="bg-gray-950/60 border-t border-gray-800/50 py-6">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <p className="text-[11px] leading-relaxed text-gray-600 font-typewriter" data-testid="landing-disclaimer">
            Deadline by Daria is an independent entertainment platform producing fictional detective thriller films and case file photosets. All content is scripted, staged, and entirely fictional — no real events, persons, or criminal activity are depicted. This site is rated 18+ due to mature themes including suspense, dark storytelling, and dramatic intensity. All content is staged and fictional. See our <a href="/content-policy" className="text-gray-500 hover:text-amber-500 underline">Content Policy</a>. By accessing this site, you confirm you are 18 years of age or older.
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-amber-900/30 bg-gray-950/80 py-6">
        <div className="container mx-auto px-4 flex flex-col sm:flex-row items-center justify-center gap-4 text-xs font-typewriter text-gray-500">
          <span>&copy; {new Date().getFullYear()} Deadline by Daria. All rights reserved.</span>
          <span className="hidden sm:inline">|</span>
          <a href="/terms" className="hover:text-amber-500 transition-colors">Terms of Service</a>
          <span className="hidden sm:inline">|</span>
          <a href="/privacy" className="hover:text-amber-500 transition-colors">Privacy Policy</a>
          <span className="hidden sm:inline">|</span>
          <a href="/content-policy" className="hover:text-amber-500 transition-colors" data-testid="landing-content-policy-link">Content Policy</a>
          <span className="hidden sm:inline">|</span>
          <a href="/help-centre" className="hover:text-amber-500 transition-colors" data-testid="landing-help-centre-link">Help Centre</a>
          <span className="hidden sm:inline">|</span>
          <a href="mailto:deadlinebydaria@gmail.com" className="hover:text-amber-500 transition-colors" data-testid="landing-contact-us-link">Contact Us</a>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
